import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import {
    Filter,
    ArrowUpDown,
    ArrowUp,
    ArrowDown,
    Trash2,
    GitCompare,
    Folder,
    Search,
    Pencil,
    PlusCircle,
    X,
} from "lucide-react";
import { api_endpoints } from "@/lib/api";
import {
    fmtCurrency,
    fmtMileage,
    fmtRelative,
    VERDICTS,
} from "@/lib/format";
import {
    PageHeader,
    VerdictBadge,
    Btn,
    Field,
    TextInput,
    Card,
    EmptyState,
} from "@/components/app/ui";

const PAGE_SIZE = 20;

export default function Dashboard() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [items, setItems] = React.useState([]);

    const [filterValues, setFilterValues] = React.useState({ fuelTypes: [], transmissions: [] });
    const [total, setTotal] = React.useState(0);
    const [page, setPage] = React.useState(1);
    const [loading, setLoading] = React.useState(true);
    const [collections, setCollections] = React.useState([]);
    const [selected, setSelected] = React.useState(new Set());
    const [filterOpen, setFilterOpen] = React.useState(true);
    const [editingNoteId, setEditingNoteId] = React.useState(null);
    const [noteDraft, setNoteDraft] = React.useState("");
    const [editingRowId, setEditingRowId] = React.useState(null);
    const [editDraft, setEditDraft] = React.useState({ price: "", mileage: "" });

    const [filters, setFilters] = React.useState(() => {
        const stored = localStorage.getItem("spotcar-dashboard-collection");
        if (stored) localStorage.removeItem("spotcar-dashboard-collection");
        return {
            make: "",
            verdict: [],
            scoreMin: "",
            scoreMax: "",
            priceMin: "",
            priceMax: "",
            mileageMin: "",
            mileageMax: "",
            yearMin: "",
            yearMax: "",
            fuelType: [],
            transmission: [],
            collections: stored ? [stored] : [],
            sortBy: "date",
            sortDir: "desc",
        };
    });

    const load = React.useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page,
                pageSize: PAGE_SIZE,
                sortBy: filters.sortBy,
                sortDir: filters.sortDir,
            };
            if (filters.make) params.make = filters.make;
            if (filters.verdict.length)
                params.verdict = filters.verdict.join(",");
            if (filters.scoreMin) params.scoreMin = filters.scoreMin;
            if (filters.scoreMax) params.scoreMax = filters.scoreMax;
            if (filters.priceMin) params.priceMin = filters.priceMin;
            if (filters.priceMax) params.priceMax = filters.priceMax;
            if (filters.mileageMin) params.mileageMin = filters.mileageMin;
            if (filters.mileageMax) params.mileageMax = filters.mileageMax;
            if (filters.yearMin) params.yearMin = filters.yearMin;
            if (filters.yearMax) params.yearMax = filters.yearMax;
            if (filters.fuelType.length)
                params.fuelType = filters.fuelType.join(",");
            if (filters.transmission.length)
                params.transmission = filters.transmission.join(",");
            if (filters.collections.length)
                params.collections = filters.collections.join(",");
            const data = await api_endpoints.history(params);
            setItems(data.items || []);
            setTotal(data.total || 0);
        } catch (e) {
            toast.error(e.message || "Failed to load history");
        } finally {
            setLoading(false);
        }
    }, [page, filters]);

    React.useEffect(() => {
        load();
    }, [load]);

    React.useEffect(() => {
        api_endpoints.collections().then(setCollections).catch(() => {});
    }, []);

    React.useEffect(() => {
        api_endpoints.historyFilterValues().then(setFilterValues).catch(() => {});
    }, []);

    function toggleArr(key, val) {
        setPage(1);
        setFilters((f) => ({
            ...f,
            [key]: f[key].includes(val)
                ? f[key].filter((v) => v !== val)
                : [...f[key], val],
        }));
    }

    function resetFilters() {
        setPage(1);
        setFilters({
            make: "",
            verdict: [],
            scoreMin: "",
            scoreMax: "",
            priceMin: "",
            priceMax: "",
            mileageMin: "",
            mileageMax: "",
            yearMin: "",
            yearMax: "",
            fuelType: [],
            transmission: [],
            collections: [],
            sortBy: "date",
            sortDir: "desc",
        });
    }

    function toggleSort(field) {
        setFilters((f) => ({
            ...f,
            sortBy: field,
            sortDir: f.sortBy === field && f.sortDir === "asc" ? "desc" : "asc",
        }));
    }

    function SortHeader({ field, align, children, className = "" }) {
        const active = filters.sortBy === field;
        const Icon = active ? (filters.sortDir === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
        return (
            <th
                className={`p-3 ${align === "right" ? "text-right" : "text-left"} cursor-pointer select-none transition-colors ${className}`}
                onClick={() => toggleSort(field)}
            >
                <span className="inline-flex items-center gap-1">
                    {children}
                    <Icon size={10} className={active ? "opacity-100" : "opacity-40"} />
                </span>
            </th>
        );
    }

    function toggleSelect(id) {
        const s = new Set(selected);
        if (s.has(id)) s.delete(id);
        else s.add(id);
        setSelected(s);
    }

    function toggleSelectAll() {
        if (selected.size === items.length) setSelected(new Set());
        else setSelected(new Set(items.map((i) => i.id)));
    }

    async function deleteSelected() {
        if (
            !window.confirm(`Delete ${selected.size} analysis(es)? This cannot be undone.`)
        )
            return;
        try {
            await Promise.all(
                Array.from(selected).map((id) => api_endpoints.historyDelete(id)),
            );
            toast.success(`Deleted ${selected.size} analyses`);
            setSelected(new Set());
            load();
        } catch (e) {
            toast.error(e.message);
        }
    }

    function compareSelected() {
        if (selected.size < 2) {
            toast.error("Select at least 2 analyses to compare.");
            return;
        }
        sessionStorage.setItem("spotcar-compare-ids", Array.from(selected).join(","));
        navigate("/app/compare");
    }

    function startNoteEdit(item) {
        setEditingNoteId(item.id);
        setNoteDraft(item.notes || "");
    }

    function startRowEdit(item) {
        setEditingRowId(item.id);
        setEditDraft({ price: item.price ?? "", mileage: item.mileage ?? "" });
    }

    async function saveRowEdit(item) {
        try {
            const updates = {};
            const newPrice = editDraft.price === "" ? null : Number(editDraft.price);
            const newMileage = editDraft.mileage === "" ? null : Number(editDraft.mileage);
            if (newPrice !== item.price) updates.price = newPrice;
            if (newMileage !== item.mileage) updates.mileage = newMileage;
            if (Object.keys(updates).length === 0) {
                setEditingRowId(null);
                return;
            }
            await api_endpoints.historyUpdate(item.id, updates);
            setItems((cur) =>
                cur.map((i) =>
                    i.id === item.id
                        ? { ...i, price: newPrice ?? i.price, mileage: newMileage ?? i.mileage }
                        : i
                )
            );
            toast.success("Updated");
        } catch (e) {
            toast.error(e.message || "Failed to update");
        } finally {
            setEditingRowId(null);
        }
    }

    function cancelRowEdit() {
        setEditingRowId(null);
    }

    async function saveNote(item) {
        try {
            await api_endpoints.historyUpdate(item.id, { notes: noteDraft });
            setItems((cur) =>
                cur.map((i) => (i.id === item.id ? { ...i, notes: noteDraft } : i)),
            );
            toast.success("Note saved");
        } catch (e) {
            toast.error(e.message);
        } finally {
            setEditingNoteId(null);
        }
    }

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
    const hasFilters = !!(
        filters.make ||
        filters.verdict.length ||
        filters.scoreMin ||
        filters.scoreMax ||
        filters.priceMin ||
        filters.priceMax ||
        filters.mileageMin ||
        filters.mileageMax ||
        filters.yearMin ||
        filters.yearMax ||
        filters.fuelType.length ||
        filters.transmission.length ||
        filters.collections.length
    );

    const pageHeader = React.useMemo(() => (
        <PageHeader
            eyebrow={t("dashboard.eyebrow")}
            title={t("dashboard.title")}
            actions={
                <>
                    <Btn
                        variant="outline"
                        icon={Filter}
                        onClick={() => setFilterOpen((v) => !v)}
                        testid="toggle-filters"
                    >
                        {filterOpen ? t("dashboard.hideFilters") : t("dashboard.showFilters")}
                    </Btn>
                    <Btn
                        variant="primary"
                        icon={PlusCircle}
                        onClick={() => navigate("/app/bulk")}
                        testid="new-analysis-btn"
                    >
                        {t("dashboard.newBulkAnalysis")}
                    </Btn>
                </>
            }
        />
    ), [t, filterOpen, navigate]);

    return (
        <div>
            {pageHeader}

            <div className="px-6 md:px-10 py-6 space-y-6">
                {/* Filters */}
                <div
                    className="grid transition-[grid-template-rows] duration-300 ease-in-out"
                    style={{ gridTemplateRows: filterOpen ? '1fr' : '0fr' }}
                >
                    <div className="overflow-hidden">
                    <Card className="p-5" testid="filters-card">
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            <Field label={t("dashboard.makeModel")} testid="make">
                                <div className="relative">
                                    <TextInput
                                        value={filters.make}
                                        onChange={(v) => {
                                            setPage(1);
                                            setFilters((f) => ({ ...f, make: v }));
                                        }}
                                        placeholder="VW Golf"
                                        testid="filter-make"
                                    />
                                    {filters.make && (
                                        <button
                                            onClick={() => {
                                                setPage(1);
                                                setFilters((f) => ({ ...f, make: "" }));
                                            }}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-black"
                                            data-testid="clear-make"
                                        >
                                            <X size={14} />
                                        </button>
                                    )}
                                </div>
                            </Field>
                            <RangeFilter
                                label={t("dashboard.score")}
                                min={0} max={100}
                                value={[Number(filters.scoreMin) || 0, Number(filters.scoreMax) || 100]}
                                onChange={([lo, hi]) => {
                                    setPage(1);
                                    setFilters((f) => ({ ...f, scoreMin: lo === 0 ? "" : String(lo), scoreMax: hi === 100 ? "" : String(hi) }));
                                }}
                                testid="range-score"
                            />
                            <RangeFilter
                                label={t("dashboard.price")}
                                min={0} max={200000} step={1000}
                                value={[Number(filters.priceMin) || 0, Number(filters.priceMax) || 200000]}
                                onChange={([lo, hi]) => {
                                    setPage(1);
                                    setFilters((f) => ({ ...f, priceMin: lo === 0 ? "" : String(lo), priceMax: hi === 200000 ? "" : String(hi) }));
                                }}
                                testid="range-price"
                            />
                            <RangeFilter
                                label={t("dashboard.year")}
                                min={2000} max={2026}
                                value={[Number(filters.yearMin) || 2000, Number(filters.yearMax) || 2026]}
                                onChange={([lo, hi]) => {
                                    setPage(1);
                                    setFilters((f) => ({ ...f, yearMin: lo === 2000 ? "" : String(lo), yearMax: hi === 2026 ? "" : String(hi) }));
                                }}
                                testid="range-year"
                            />
                            <RangeFilter
                                label={t("dashboard.mileage")}
                                min={0} max={500000} step={5000}
                                value={[Number(filters.mileageMin) || 0, Number(filters.mileageMax) || 500000]}
                                onChange={([lo, hi]) => {
                                    setPage(1);
                                    setFilters((f) => ({ ...f, mileageMin: lo === 0 ? "" : String(lo), mileageMax: hi === 500000 ? "" : String(hi) }));
                                }}
                                testid="range-mileage"
                            />
                        </div>

                        {/* Pill rows */}
                        <div className="mt-5 space-y-3">
                            <PillRow
                                label={t("dashboard.filterVerdict")}
                                options={VERDICTS.map((v) => ({ v, label: v }))}
                                selected={filters.verdict}
                                onToggle={(v) => toggleArr("verdict", v)}
                                testid="pill-verdict"
                            />
                            <PillRow
                                label={t("dashboard.filterFuel")}
                                options={filterValues.fuelTypes.map((v) => ({ v, label: v }))}
                                selected={filters.fuelType}
                                onToggle={(v) => toggleArr("fuelType", v)}
                                testid="pill-fuel"
                            />
                            <PillRow
                                label={t("dashboard.filterTransmission")}
                                options={filterValues.transmissions.map((v) => ({ v, label: v }))}
                                selected={filters.transmission}
                                onToggle={(v) => toggleArr("transmission", v)}
                                testid="pill-transmission"
                            />
                            {collections.length > 0 && (
                                <PillRow
                                    label={t("dashboard.filterCollection")}
                                    options={collections.map((c) => ({
                                        v: c.id,
                                        label: c.name,
                                    }))}
                                    selected={filters.collections}
                                    onToggle={(v) => toggleArr("collections", v)}
                                    testid="pill-collections"
                                />
                            )}
                        </div>

                        {hasFilters && (
                            <div className="mt-5 flex">
                                <Btn
                                    variant="ghost"
                                    icon={X}
                                    onClick={resetFilters}
                                    testid="reset-filters"
                                    size="sm"
                                >
                                    {t("dashboard.resetFilters")}
                                </Btn>
                            </div>
                        )}
                    </Card>
                    </div>
                </div>

                {/* Batch bar */}
                {selected.size > 0 && (
                    <div
                        className="border-2 border-black bg-[#FFCC00] p-3 flex flex-wrap items-center gap-3"
                        data-testid="batch-bar"
                    >
                        <span className="font-mono text-xs uppercase tracking-[0.2em]">
                            {t("dashboard.selectedCount", { count: selected.size })}
                        </span>
                        <Btn
                            variant="primary"
                            icon={GitCompare}
                            onClick={compareSelected}
                            size="sm"
                            testid="batch-compare"
                        >
                            {t("dashboard.compare")}
                        </Btn>
                        <Btn
                            variant="danger"
                            icon={Trash2}
                            onClick={deleteSelected}
                            size="sm"
                            testid="batch-delete"
                        >
                            {t("dashboard.delete")}
                        </Btn>
                        <Btn
                            variant="ghost"
                            onClick={() => setSelected(new Set())}
                            size="sm"
                            testid="batch-clear"
                        >
                            {t("dashboard.clear")}
                        </Btn>
                    </div>
                )}

                {/* Table */}
                {loading && items.length === 0 ? (
                    <Card className="p-10 text-center font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">
                        Loading…
                    </Card>
                ) : items.length === 0 && !loading ? (
                    <EmptyState
                        icon={Search}
                        title={t("dashboard.noAnalyses")}
                        description={
                            hasFilters
                                ? t("dashboard.noAnalysesFiltered")
                                : t("dashboard.noAnalysesEmpty")
                        }
                        action={
                            <Btn
                                variant="primary"
                                icon={PlusCircle}
                                onClick={() => navigate("/app/bulk")}
                                testid="empty-cta"
                            >
                                {t("dashboard.newBulkAnalysis")}
                            </Btn>
                        }
                    />
                ) : (
                    <Card testid="history-table-card" className="overflow-hidden">
                        <div className="overflow-x-auto" style={{ scrollbarGutter: "stable" }}>
                            <table className="w-full text-sm" data-testid="history-table">
                                <thead className="border-b-2 border-black bg-zinc-50">
                                    <tr className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
                                        <th className="p-3 text-left w-10 group/hdr">
                                            <input
                                                type="checkbox"
                                                checked={
                                                    selected.size === items.length &&
                                                    items.length > 0
                                                }
                                                onChange={toggleSelectAll}
                                                data-testid="select-all"
                                                className={`h-3.5 w-3.5 accent-black transition-opacity ${selected.size > 0 ? 'opacity-100' : 'opacity-0 group-hover/hdr:opacity-100'}`}
                                            />
                                        </th>
                                        <SortHeader field="make" className="w-1/5">{t("dashboard.colCar")}</SortHeader>
                                        <SortHeader field="score">{t("dashboard.colScore")}</SortHeader>
                                        <SortHeader field="verdict">{t("dashboard.colVerdict")}</SortHeader>
                                        <SortHeader field="price" align="right">{t("dashboard.colPrice")}</SortHeader>
                                        <SortHeader field="mileage" align="right">{t("dashboard.colMileage")}</SortHeader>
                                        <th className="p-3 text-left w-[15%] min-w-[150px]">{t("dashboard.colNotes")}</th>
                                        <SortHeader field="date">{t("dashboard.colAdded")}</SortHeader>
                                        <th className="p-3 w-12"></th>
                                    </tr>
                                </thead>
                                <tbody className={loading ? "opacity-50 pointer-events-none transition-opacity" : "transition-opacity"}>
                                    {items.map((it) => (
                                        <tr
                                            key={it.id}
                                            data-testid={`row-${it.id}`}
                                            className={`border-b border-zinc-200 dark:border-zinc-700 last:border-b-0 transition-colors group cursor-pointer ${selected.has(it.id) ? 'bg-amber-100 hover:bg-amber-200 dark:bg-zinc-800 dark:hover:bg-zinc-700' : 'hover:bg-[var(--surface-container-high)]'}`}
                                            onClick={(e) => {
                                                if (e.target.closest('a, button, input')) return;
                                                toggleSelect(it.id);
                                            }}
                                        >
                                            <td className="p-3">
                                                <input
                                                    type="checkbox"
                                                    checked={selected.has(it.id)}
                                                    onChange={() => toggleSelect(it.id)}
                                                    data-testid={`select-${it.id}`}
                                                    className={`h-3.5 w-3.5 accent-black transition-opacity ${selected.has(it.id) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                                                />
                                            </td>
                                            <td className="p-3">
                                                <Link
                                                    to={`/app/analysis/${it.id}`}
                                                    className="font-display uppercase tracking-tight hover:underline"
                                                >
                                                    {it.make} {it.model}
                                                </Link>
                                                <div className="font-mono text-[10px] text-zinc-500 mt-0.5">
                                                    {it.year} · {it.fuelType} · {it.transmission}
                                                </div>
                                            </td>
                                            <td className="p-3 font-display text-lg">
                                                {it.score}
                                            </td>
                                            <td className="p-3">
                                                <VerdictBadge
                                                    verdict={it.verdict}
                                                    size="sm"
                                                />
                                            </td>
                                            <td className="p-3 text-right font-mono">
                                                {editingRowId === it.id ? (
                                                    <TextInput
                                                        type="number"
                                                        value={editDraft.price}
                                                        onChange={(v) => setEditDraft((d) => ({ ...d, price: v }))}
                                                        autoFocus
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") saveRowEdit(it);
                                                            if (e.key === "Escape") cancelRowEdit();
                                                        }}
                                                        testid={`price-edit-${it.id}`}
                                                    />
                                                ) : (
                                                    fmtCurrency(it.price, it.currency)
                                                )}
                                            </td>
                                            <td className="p-3 text-right font-mono">
                                                {editingRowId === it.id ? (
                                                    <TextInput
                                                        type="number"
                                                        value={editDraft.mileage}
                                                        onChange={(v) => setEditDraft((d) => ({ ...d, mileage: v }))}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") saveRowEdit(it);
                                                            if (e.key === "Escape") cancelRowEdit();
                                                        }}
                                                        testid={`mileage-edit-${it.id}`}
                                                    />
                                                ) : (
                                                    fmtMileage(it.mileage, it.mileageUnit)
                                                )}
                                            </td>
                                            <td className="p-3 max-w-[220px]">
                                                {editingNoteId === it.id ? (
                                                    <div className="flex flex-col gap-1">
                                                        <textarea
                                                            value={noteDraft}
                                                            onChange={(e) => setNoteDraft(e.target.value)}
                                                            autoFocus
                                                            aria-label={t("dashboard.addNote")}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Escape") setEditingNoteId(null);
                                                                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") saveNote(it);
                                                            }}
                                                            data-testid={`note-edit-${it.id}`}
                                                            className="w-full border-2 border-black bg-white p-2 font-mono text-xs focus:outline-none focus:border-[#FFCC00] min-h-[80px] resize-y"
                                                            placeholder={t("dashboard.addNote")}
                                                        />
                                                        <div className="flex gap-1">
                                                            <Btn variant="primary" size="sm" onClick={() => saveNote(it)} testid={`note-save-${it.id}`}>
                                                                {t("dashboard.save")}
                                                            </Btn>
                                                            <Btn variant="ghost" size="sm" onClick={() => setEditingNoteId(null)} testid={`note-cancel-${it.id}`}>
                                                                {t("dashboard.cancel")}
                                                            </Btn>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => startNoteEdit(it)}
                                                        data-testid={`note-${it.id}`}
                                                        className={`text-left text-xs text-zinc-700 whitespace-pre-wrap break-words w-full hover:underline transition-opacity ${!it.notes ? "opacity-0 group-hover:opacity-100" : ""}`}
                                                        title={t("dashboard.addNote")}
                                                    >
                                                        {it.notes ? (
                                                            <span className="ph-mask">{it.notes}</span>
                                                        ) : (
                                                            <span className="text-zinc-400 italic">
                                                                {t("dashboard.addNote")}
                                                            </span>
                                                        )}
                                                    </button>
                                                )}
                                            </td>
                                            <td className="p-3 font-mono text-[10px] text-zinc-500 whitespace-nowrap">
                                                {fmtRelative(it.createdAt)}
                                            </td>
                                            <td className="p-3 text-right whitespace-nowrap md:opacity-0 md:group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
                                                {editingRowId === it.id ? (
                                                    <span className="inline-flex items-center gap-2">
                                                        <Btn variant="primary" size="sm" onClick={() => saveRowEdit(it)} testid={`save-${it.id}`}>
                                                            {t("dashboard.save")}
                                                        </Btn>
                                                        <Btn variant="ghost" size="sm" onClick={cancelRowEdit} testid={`cancel-${it.id}`}>
                                                            {t("dashboard.cancel")}
                                                        </Btn>
                                                    </span>
                                                ) : (
                                                    <button
                                                        onClick={() => startRowEdit(it)}
                                                        data-testid={`edit-${it.id}`}
                                                        className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] hover:underline"
                                                    >
                                                        <Pencil size={12} /> {t("dashboard.edit")}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </Card>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between font-mono text-xs uppercase tracking-[0.2em]">
                        <span data-testid="page-info">
                            {t("dashboard.pageInfo", { page, totalPages, total })}
                        </span>
                        <div className="flex gap-2">
                            <Btn
                                variant="outline"
                                size="sm"
                                disabled={page <= 1}
                                onClick={() => setPage((p) => p - 1)}
                                testid="prev-page"
                            >
                                {t("dashboard.prev")}
                            </Btn>
                            <Btn
                                variant="outline"
                                size="sm"
                                disabled={page >= totalPages}
                                onClick={() => setPage((p) => p + 1)}
                                testid="next-page"
                            >
                                {t("dashboard.next")}
                            </Btn>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function RangeFilter({ label, min, max, value, onChange, testid, step = 1 }) {
    return (
        <Field label={label} testid={testid}>
            <div className="px-1 pt-3 pb-1">
                <Slider
                    range
                    min={min}
                    max={max}
                    step={step}
                    value={value}
                    onChange={onChange}
                    ariaLabelForHandle={[`${label} minimum`, `${label} maximum`]}
                    styles={{
                        track: { backgroundColor: "#FFCC00" },
                        handle: { borderColor: "#FFCC00", backgroundColor: "#FFCC00", opacity: 1 },
                        rail: { backgroundColor: "var(--outline, #9ca3af)" },
                    }}
                />
                <div className="flex justify-between mt-1.5 font-mono text-[10px] text-zinc-500">
                    <span>{value[0]}</span>
                    <span>{value[1]}</span>
                </div>
            </div>
        </Field>
    );
}

function PillRow({ label, options, selected, onToggle, testid }) {
    return (
        <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 items-start" data-testid={testid}>
            <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-zinc-500 pt-1 min-w-[100px]">
                {label}
            </span>
            <div className="flex flex-wrap gap-2">
                {options.map((o) => {
                    const on = selected.includes(o.v);
                    return (
                        <button
                            key={o.v}
                            onClick={() => onToggle(o.v)}
                            data-testid={`${testid}-${o.v}`}
                            className={`border-2 border-black px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${on ? "bg-black text-white dark:bg-[#FFCC00] dark:!text-[#000]" : "bg-white hover:bg-[var(--surface-container-high)]"}`}
                        >
                            {o.label}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
